import { Fragment, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Menu, Transition } from '@headlessui/react';
import { SparklesIcon, ShieldCheckIcon, ChevronDownIcon, ArrowTopRightOnSquareIcon, TrashIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import type { Client } from '@shared/types';
import { US_STATES, COUNTRIES } from '@shared/constants';
import { useClientContacts } from '../../hooks/useClientContacts';
import { useConfirm } from '../../hooks/useConfirm';
import { useTemplates } from '../../hooks/useTemplates';
import { ContactsList } from './ContactsList';
import { EmailAcordModal } from './EmailAcordModal';
import { clientService } from '../../services/clientService';
import { acordService, type AcordCertificate } from '../../services/acordService';

interface ClientEditorProps {
  client: Client | null;
  onSave: (client: Partial<Client>) => void;
  onCancel: () => void;
  saving?: boolean;
}

export function ClientEditor({ client, onSave, onCancel, saving = false }: ClientEditorProps) {
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [entityType, setEntityType] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('US');
  const [website, setWebsite] = useState('');
  const [industry, setIndustry] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive' | 'prospect'>('active');
  const [enriching, setEnriching] = useState(false);
  const [generatingAcord, setGeneratingAcord] = useState(false);
  const [acordCerts, setAcordCerts] = useState<AcordCertificate[]>([]);
  const [acordCertsLoading, setAcordCertsLoading] = useState(false);
  const [deletingCertId, setDeletingCertId] = useState<string | null>(null);
  const [emailCert, setEmailCert] = useState<AcordCertificate | null>(null);
  const { confirm, ConfirmDialog } = useConfirm();

  // Load contacts for existing clients
  const { contacts, reload: reloadContacts } = useClientContacts(client?.id || '');

  // Load templates so we can offer ACORD generation for saved clients that
  // have an address. ACORD templates that are inactive are hidden.
  const { templates } = useTemplates();
  const acordTemplates = templates.filter(
    (t) => t.templateType === 'ACORD' && t.isActive,
  );
  const hasAddress = Boolean(addressLine1 || city || state || zip);
  const canGenerateAcord =
    !!client && hasAddress && acordTemplates.length > 0 && !generatingAcord;

  const loadAcordCerts = async (clientId: string) => {
    setAcordCertsLoading(true);
    try {
      const certs = await acordService.listCertificates(clientId);
      setAcordCerts(certs);
    } catch (error) {
      // Don't toast — the cert list is a nice-to-have on this page, and we
      // don't want it spamming errors if SharePoint is misconfigured.
      console.warn('Failed to load ACORD certificates:', error);
      setAcordCerts([]);
    } finally {
      setAcordCertsLoading(false);
    }
  };

  // Refresh the cert list whenever the selected client changes (and on first load).
  useEffect(() => {
    if (client?.id) {
      void loadAcordCerts(client.id);
    } else {
      setAcordCerts([]);
    }
  }, [client?.id]);

  const handleDeleteCert = async (cert: AcordCertificate) => {
    if (!client) return;
    const ok = await confirm({
      title: 'Delete Certificate',
      message: `Permanently delete "${cert.name}" from SharePoint? This can't be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;

    setDeletingCertId(cert.file_id);
    const toastId = toast.loading('Deleting certificate…');
    try {
      await acordService.deleteCertificate(cert.file_id);
      toast.success('Certificate deleted', { id: toastId });
      void loadAcordCerts(client.id);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to delete certificate',
        { id: toastId },
      );
    } finally {
      setDeletingCertId(null);
    }
  };

  const handleGenerateAcord = async (templateId: string) => {
    if (!client) return;
    setGeneratingAcord(true);
    const toastId = toast.loading('Generating ACORD certificate…');
    try {
      const result = await acordService.generateCertificate(client.id, templateId);
      toast.success(
        <span>
          Certificate generated.{' '}
          <a
            href={result.web_url}
            target="_blank"
            rel="noreferrer noopener"
            className="underline font-medium"
          >
            Open in SharePoint
          </a>
        </span>,
        { id: toastId, duration: 10000 },
      );
      // Refresh the list so the new cert shows up below.
      void loadAcordCerts(client.id);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to generate certificate',
        { id: toastId },
      );
    } finally {
      setGeneratingAcord(false);
    }
  };

  useEffect(() => {
    if (client) {
      setName(client.name);
      setShortName(client.shortName || '');
      setEntityType(client.entityType || '');
      setLogoUrl(client.logoUrl || '');
      setAddressLine1(client.addressLine1 || '');
      setAddressLine2(client.addressLine2 || '');
      setCity(client.city || '');
      setState(client.state || '');
      setZip(client.zip || '');
      setCountry(client.country || 'US');
      setWebsite(client.website || '');
      setIndustry(client.industry || '');
      setNotes(client.notes || '');
      setStatus(client.status);
    } else {
      setName('');
      setShortName('');
      setEntityType('');
      setLogoUrl('');
      setAddressLine1('');
      setAddressLine2('');
      setCity('');
      setState('');
      setZip('');
      setCountry('US');
      setWebsite('');
      setIndustry('');
      setNotes('');
      setStatus('active');
    }
  }, [client]);

  const handleEnrich = async () => {
    if (!name.trim()) {
      toast.error('Please enter a company name first');
      return;
    }

    setEnriching(true);
    const toastId = toast.loading('✨ Enriching with AI...');

    try {
      // Send everything we currently know so the AI doesn't overwrite us and
      // can skip fields we've already populated. Country is part of the
      // payload now so the AI uses the right state/region format (2-letter
      // for US, free text everywhere else).
      const enriched = await clientService.enrichClient(name, {
        shortName,
        entityType,
        website,
        industry,
        addressLine1,
        city,
        state,
        zip,
        country,
      });

      // Tuples of [predicate (field is empty), incoming value, setter, label]
      // — keeps the apply + summary logic in one pass.
      //
      // Country is treated as "empty" only when the user hasn't picked
      // anything other than the default 'US'. Otherwise (the user actively
      // selected a country, even US) we don't override it.
      const countryIsDefault = !country || country === 'US';
      const updates: Array<[boolean, string | undefined, (v: string) => void, string]> = [
        [!shortName,        enriched.shortName,           setShortName,    'Short Name'],
        [!entityType,       enriched.entityType,          setEntityType,   'Entity Type'],
        [!website,          enriched.website,             setWebsite,      'Website'],
        [!industry,         enriched.industry,            setIndustry,     'Industry'],
        [!logoUrl,          enriched.logoUrl,             setLogoUrl,      'Logo'],
        [countryIsDefault,  enriched.headquartersCountry, setCountry,      'Country'],
        [!addressLine1,     enriched.headquartersAddress, setAddressLine1, 'Address'],
        [!city,             enriched.headquartersCity,    setCity,         'City'],
        [!state,            enriched.headquartersState,   setState,        'State'],
        [!zip,              enriched.headquartersZip,     setZip,          'ZIP'],
      ];

      const labelsApplied: string[] = [];
      for (const [empty, value, setter, label] of updates) {
        if (empty && value) {
          setter(value);
          labelsApplied.push(label);
        }
      }

      if (labelsApplied.length > 0) {
        toast.success(`✨ Added: ${labelsApplied.join(', ')}`, { id: toastId });
      } else {
        toast('All fields already filled', { id: toastId, icon: 'ℹ️' });
      }
    } catch (error) {
      console.error('Failed to enrich client:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to enrich client data';
      toast.error(errorMessage, { id: toastId });
    } finally {
      setEnriching(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name,
      shortName,
      entityType,
      logoUrl,
      addressLine1,
      addressLine2,
      city,
      state,
      zip,
      country,
      website,
      industry,
      notes,
      status,
    });
  };

  return (
    <div className="bg-white shadow-sm rounded-lg p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {client ? 'Edit Client' : 'New Client'}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {/* ACORD generation. Only shown for saved clients (we need an id)
              with at least one address line + an active ACORD template.
              When one template is active: plain button. When multiple are
              active: dropdown so the user picks the coverage type. */}
          {client && acordTemplates.length === 1 && (
            <button
              type="button"
              onClick={() => handleGenerateAcord(acordTemplates[0].id)}
              disabled={!canGenerateAcord}
              className="flex items-center gap-2 px-4 py-2 border border-amber-600 text-amber-700 rounded-md hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                !hasAddress
                  ? 'Add the client address first — the certificate names them as holder.'
                  : `Generate ACORD certificate from "${acordTemplates[0].name}"`
              }
            >
              <ShieldCheckIcon className={`w-5 h-5 ${generatingAcord ? 'animate-spin' : ''}`} />
              {generatingAcord ? 'Generating…' : 'Generate ACORD'}
            </button>
          )}
          {client && acordTemplates.length > 1 && (
            <Menu as="div" className="relative">
              <Menu.Button
                disabled={!canGenerateAcord}
                className="flex items-center gap-2 px-4 py-2 border border-amber-600 text-amber-700 rounded-md hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title={
                  !hasAddress
                    ? 'Add the client address first — the certificate names them as holder.'
                    : 'Pick which ACORD coverage to generate'
                }
              >
                <ShieldCheckIcon className={`w-5 h-5 ${generatingAcord ? 'animate-spin' : ''}`} />
                {generatingAcord ? 'Generating…' : 'Generate ACORD'}
                <ChevronDownIcon className="w-4 h-4" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-72 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
                  <div className="py-1">
                    {acordTemplates.map((t) => (
                      <Menu.Item key={t.id}>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => handleGenerateAcord(t.id)}
                            className={`w-full text-left px-4 py-2 text-sm ${
                              active ? 'bg-amber-50 text-amber-900' : 'text-gray-700'
                            }`}
                          >
                            {t.name}
                            {t.description && (
                              <span className="block text-xs text-gray-500 truncate">{t.description}</span>
                            )}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          )}

          <button
            type="button"
            onClick={handleEnrich}
            disabled={enriching || !name.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-md hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            title="Use AI to fill in missing fields"
          >
            <SparklesIcon className={`w-5 h-5 ${enriching ? 'animate-spin' : ''}`} />
            {enriching ? 'Enriching...' : 'Enrich with AI'}
          </button>
        </div>
      </div>

      {/* These are CLIENT addresses, not the user's own, so we don't want
          Chrome offering to save them to the user's Google Account. The
          reliable suppressor is autoComplete="new-password" on each address-
          cluster input — Chrome's address heuristic explicitly skips
          new-password fields, which "off" and unknown values do not. */}
      <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Company Name *
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label htmlFor="shortName" className="block text-sm font-medium text-gray-700 mb-1">
              Short Name
            </label>
            <input
              type="text"
              id="shortName"
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
              placeholder="e.g., MS, IBM"
              maxLength={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Used for file naming (e.g., "MS_SOW_2025-01-15.docx")
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="entityType" className="block text-sm font-medium text-gray-700 mb-1">
              Entity Type
            </label>
            <input
              type="text"
              id="entityType"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              placeholder="LLC, Corporation, Partnership, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-xs text-gray-500 mt-1">Legal entity type (e.g., LLC, Corporation)</p>
          </div>

          <div>
            <label htmlFor="logoUrl" className="block text-sm font-medium text-gray-700 mb-1">
              Logo URL
            </label>
            <input
              type="url"
              id="logoUrl"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://example.com/logo.png"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {logoUrl && (
              <div className="mt-2">
                <img 
                  src={logoUrl} 
                  alt="Company logo" 
                  className="h-12 w-12 object-contain border border-gray-200 rounded"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Country goes first because it controls the labels and the type
            of the State/Region field below. Defaults to US. */}
        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
            Country
          </label>
          <select
            id="country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            autoComplete="new-password"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="addressLine1" className="block text-sm font-medium text-gray-700 mb-1">
            Street Address
          </label>
          <input
            type="text"
            id="addressLine1"
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
            placeholder="Street address"
            autoComplete="new-password"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="addressLine2" className="block text-sm font-medium text-gray-700 mb-1">
            Address Line 2 (Optional)
          </label>
          <input
            type="text"
            id="addressLine2"
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
            placeholder="Suite, apt, building, floor, etc."
            autoComplete="new-password"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* City / State (or Region) / ZIP (or Postal Code).
            US gets the state dropdown + ZIP label.
            Anywhere else gets a free-text region input + Postal Code label;
            postal codes can be long ("75008-XXXX" in France, etc.). */}
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
          <div className="col-span-3">
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
              City
            </label>
            <input
              type="text"
              id="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="col-span-2">
            <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
              {country === 'US' ? 'State' : 'State / Region'}
            </label>
            {country === 'US' ? (
              <select
                id="state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                autoComplete="new-password"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select...</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                id="state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="Province / Region"
                autoComplete="new-password"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            )}
          </div>

          <div className="col-span-1">
            <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-1">
              {country === 'US' ? 'ZIP' : 'Postal Code'}
            </label>
            <input
              type="text"
              id="zip"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              maxLength={country === 'US' ? 10 : 20}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              type="url"
              id="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1">
              Industry
            </label>
            <select
              id="industry"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select industry...</option>
              <option value="Technology">Technology</option>
              <option value="Finance">Finance</option>
              <option value="Healthcare">Healthcare</option>
              <option value="Manufacturing">Manufacturing</option>
              <option value="Retail">Retail</option>
              <option value="Education">Education</option>
              <option value="Real Estate">Real Estate</option>
              <option value="Consulting">Consulting</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>


        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="active">Active</option>
            <option value="prospect">Prospect</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Additional notes about this client..."
          />
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Client'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </form>

      {/* ACORD Certificates — only for saved clients with at least one
          generated cert (or while loading). Newest first. */}
      {client && (acordCertsLoading || acordCerts.length > 0) && (
        <div className="border-t pt-6 mt-6">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheckIcon className="w-5 h-5 text-amber-600" />
            <h3 className="text-lg font-semibold text-gray-900">Insurance Certificates</h3>
            {acordCertsLoading && (
              <span className="text-xs text-gray-400">Loading…</span>
            )}
          </div>
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
            {acordCerts.map((c) => (
              <li
                key={c.file_id}
                className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-gray-50"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">{c.name}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(c.last_modified).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <a
                    href={c.web_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 px-2 py-1 text-sm text-amber-700 hover:bg-amber-50 rounded"
                    title="Open in SharePoint"
                  >
                    Open
                    <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                  </a>
                  <button
                    type="button"
                    onClick={() => setEmailCert(c)}
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                    title="Email certificate to a contact"
                  >
                    <EnvelopeIcon className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteCert(c)}
                    disabled={deletingCertId === c.file_id}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                    title="Delete certificate from SharePoint"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Contacts Section - Only show for existing clients */}
      {client && (
        <ContactsList
          clientId={client.id}
          contacts={contacts}
          onReload={reloadContacts}
        />
      )}

      <ConfirmDialog />

      {client && (
        <EmailAcordModal
          isOpen={!!emailCert}
          onClose={() => setEmailCert(null)}
          client={client}
          certificate={emailCert}
        />
      )}
    </div>
  );
}

